import { computed, reactive } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import { useI18n } from 'vue-i18n';
import { scrollEase } from '@/smoothScroll';
import { trackChat } from '@/analytics';
import { reset as resetTurnstile, token as turnstileToken } from '@/turnstile';

/* ---------------------------------------------------------------------------
   The website assistant, as a conversation rather than as a component.

   The state lives at module scope, not inside a component, and that is the one
   decision everything else follows from. The public layout renders on every
   page, so an Inertia visit unmounts and remounts the launcher; a component
   local thread would be thrown away and restarted on each one, and a visitor who
   asked something on the home page would find the AI page had never heard of it.
   One thread, drawn by however many surfaces are on screen.

   Module scope is also what makes the AI page work. That page carries two
   surfaces over this thread at once, the box in its own band and the floating
   launcher, and they stay in step because neither of them holds any of it. The
   transcript, the chips, the draft, the streaming answer and the reasoning
   disclosure are all here, so there is no second copy to fall out of step and
   nothing to synchronise: a change to any of them is a change to the one value
   both surfaces are reading.

   What this owns: the transcript, the chips, the composer, the reasoning
   disclosure, the guard against a second send, the server-sent-event reader, and
   the first load of history. What it does not own: any words. The panel
   translates everything, so no two surfaces can word the same state differently.

   Nothing here is ever aborted. The layout remounts on every Inertia visit, so
   aborting on unmount would throw away the reply the visitor just asked for.
   The request runs to its end, the server persists the finished answer, and
   reopening the panel restores it from history. Only a real page unload ends it.
--------------------------------------------------------------------------- */

const state = reactive({
  /* `{ id, role, text, reasoning }` per turn, oldest first. The role is `bot` or
     `user`, and `reasoning` is `{ text, ms }` on a bot turn the model narrated,
     null otherwise. A finished turn's summary is stored with the answer and
     comes back through history. */
  messages: [],

  /* The newest chip labels, or an empty array. `chipMode` says whether they
     are answers to the question just asked, or the usual follow-up questions. */
  suggestions: [],
  chipMode: 'suggestions',

  /* A question has been asked and its answer has not finished. */
  isTyping: false,
  /* True from the click until the check has a token. The send button spins on
     this. `isTyping` stays false so a check the visitor walks away from never
     leaves a bubble behind. */
  checking: false,
  /* The checkbox is on screen. The wait line comes down; the button keeps
     spinning until the tick comes back. */
  challengeOpen: false,

  /* The part of the answer that has arrived from the server. This is the whole
     reply so far, and it is not what the panel draws. See `streamingShown`. */
  streamingText: '',

  /* The part of the answer that has been revealed on screen: a prefix of
     `streamingText`, and this is what a surface renders.

     The pair exists because the network is not an animation. Deltas arrive at
     whatever rate the model produces them, and for a fast model that is the
     whole answer inside two or three frames; drawn straight from the network,
     a good reply appears fully formed and reads as a page refresh rather than as
     writing. The reveal is what makes the arrival visible. It can only ever be a
     prefix, because an answer only ever grows. */
  streamingShown: '',

  /* The narration, received and revealed, the same pair as the answer's.

     These two are the live copy. When the answer lands, the text moves onto
     the finished message, and history brings that same text back, so a reload
     still has the trace. */
  reasoningText: '',
  reasoningShown: '',
  reasoningActive: false,
  pendingCards: [],
  liveCards: [],
  /* A pack is on the way, so the message stays hidden until the cards have
     been drawn. */
  cardsBeforeText: false,

  /* The narration has finished arriving and the answer has started, so the trace
     is due to fold once the visitor has actually seen the whole of it.

     A flag rather than the fold itself, because the narration is drawn at a
     bounded rate and may still be catching up when the thinking ends. Folding on
     the token that ended it took the last of the narration away before it had
     been written on screen, which is the one thing the reveal exists to stop. */
  reasoningDone: false,

  /* The stream has finished, so what has arrived is now the whole answer, and
     the reveal is still catching up.

     This is what turns the reveal from a live catch-up into a bounded finish.
     Without it the catch-up would simply be cut off when the connection closes,
     and the tail of every answer would appear in one jump. */
  streamDone: false,

  /* How long that narration took, in milliseconds, from its first token to the
     first token of the answer. The server measures it, sends it on
     `reasoning_complete`, and stores the same number, so a reload shows it. */
  reasoningMs: 0,

  /* Which reasoning traces have been opened, keyed by message index, and whether
     the live one is open.

     Shared rather than kept inside the surface, because the disclosure is part
     of the conversation. The AI page now draws this thread twice, and the two
     drawings are one view of it: a trace opened in one has to be open in the
     other, and the automatic fold when an answer starts has to happen in both
     at once rather than one panel closing while the other stays open. */
  openThinks: {},
  thinkOpen: true,

  /* The message being typed.

     Also shared, and for the same reason: one thread means one composer. Text
     typed in one surface has to be there in the other, and sending from either
     has to clear both, or one surface is left holding a question the other has
     already asked. The field is the only writer. */
  draft: '',

  /* Whether the assistant can answer at all. `null` is "not known yet", which
     is deliberately not the same as `false`: an unanswered question about
     availability must not disable the field. */
  available: null,

  /* The last thing that went wrong, as a key rather than as a sentence, so the
     copy lives with the rest of the panel's copy. Empty when nothing has. */
  notice: '',

  /* Whether a suggestion call is in flight.

     A guard against a second call, and nothing else: the fold is driven by
     `suggestionsStale` below. It was briefly what drove the surface, and that
     is the bug this pair is arranged to avoid, because a request being in
     flight says nothing about whether the row on screen is still the right
     one. */
  suggesting: false,

  /* Whether the chips on screen have been overtaken by a turn newer than they
     are.

     This is what folds the chip row, and it is deliberately not the same thing
     as a suggestion call being in flight. Hanging the fold off the request made
     the row flash on every open: opening the panel asked for the chips again,
     so the row folded for the length of that request and came straight back
     when it landed, even though the set already on screen was the correct one.

     A row is only worth hiding when it is out of date, which is true from the
     moment a question is sent until the set for its answer has arrived. */
  suggestionsStale: false,

  /* The history has been asked for, once, and will not be asked for again. */
  loaded: false,

  /* The history request has settled, so the panel knows whether this thread is
     empty or has turns to restore.

     Distinct from `loaded`, which only means the request was sent. Without this
     the panel cannot tell "there is no conversation" from "not answered yet",
     and it showed its greeting while the request was in flight and then
     replaced it with the transcript a moment later. */
  hydrated: false,
});

/* The live global locale. Read at send time rather than captured on mount.
   A language switch is a visit to another URL, and the composer follows that
   URL. A question asked after the visit has to leave in the language of the
   page the visitor is on, not the one they started on. */
let localeRef = null;
let pageRef = null;

/* The public site key, or an empty string where this environment is not
   running the check. Read at send time so a later visit still sees the page
   that is on screen. */
function turnstileSitekey() {
  if (!pageRef || !pageRef.props) {
    return '';
  }

  const props = pageRef.props.value || pageRef.props;
  const turnstile = props && props.turnstile;

  return turnstile && turnstile.sitekey ? String(turnstile.sitekey) : '';
}

/* Which surfaces are on screen, by name.

   A set rather than a boolean, and the AI page is why. It carries two surfaces
   over this one thread: the box in its own band and the floating panel. A
   boolean was enough while a page had at most one, and it fails here in a way
   that is not obvious: closing the floating panel would set it false while the
   band was still on screen, and the band would then refuse to ask for its
   chips. A set cannot express that state, because a surface is visible while
   its own name is in here and no other surface can take it out.

   Neither name is ever left behind. The launcher adds `panel` when it opens and
   removes it when it closes, and again if it unmounts while open; the AI page's
   band adds `band` on mount and removes it on unmount. */
const visibleSurfaces = new Set();

/* The history request. One promise for every caller, so a send that starts
   while it is still in flight waits for the cookie it sets instead of minting
   a second visitor id of its own. */
let historyReady = Promise.resolve();

/* Set for the whole of a message request. A history retry must not run beside
   it: both would mint a visitor id, and the later Set-Cookie would orphan the
   transcript. */
let naming = false;

/* Bumped when a question is sent. A chip response from before that send is for
   the previous turn and must not land on top of the set the answer is owed. */
let suggestionEpoch = 0;

/* A chip request was wanted while one was already running. The in-flight one
   finishes, then this asks again, so the set for the newer turn is not dropped
   on the floor because the previous request still held the guard. */
let suggestionQueued = false;

/* Held from the click until the check has returned a token. `isTyping` cannot
   do this job: that flag is the loading bubble, and it must not start until
   the visitor has finished the check. */
let sendLock = false;

/* ---------------------------------------------------------------------------
   The reveal: how fast the answer is drawn, as distinct from how fast it
   arrives.

   One frontier per stream, and both are advanced from a single frame loop here
   rather than from the network callback. The answer is held until the narration
   has been drawn in full, even when the reply has already arrived: the trace
   finishes, then the reply is written, so the two never share the log.
--------------------------------------------------------------------------- */

/* How fast either frontier is revealed while the stream is live.

   A cap, not a target. A model slower than this sets the pace and the reveal
   simply keeps up with it, one delta behind. A model faster than this is held
   down to it, which is the point: the alternative is that an answer lands in two
   frames and reads as a dump rather than as writing. 400 characters per second
   is roughly a fast typist sustained, and puts a typical 900 character answer on
   screen over about two seconds. This is the one number to change if the pacing
   wants to feel quicker or more deliberate.

   The narration is drawn at this same rate rather than a faster one of its own,
   and the answer does not start until that drawing has finished. A trace that
   was still being written while the reply appeared beside it read as two things
   arriving at once. */
const REVEAL_CHARS_PER_SEC = 400;

/* The share of the outstanding text covered per 60Hz frame. This is what makes
   the reveal decelerate as it closes on the frontier rather than holding the cap
   all the way, so a slow model's own rhythm is not re-timed by us. */
const REVEAL_APPROACH = 0.22;

/* The finish. Once the stream is done there is a fixed backlog to clear, and it
   is cleared over a bounded time rather than by the approach curve above, which
   approaches its target without ever reaching it. The floor is what keeps a
   one-line answer reading as writing rather than a blink; the ceiling is what
   stops a long backlog becoming a wait after the reply has already finished. */
const FINISH_MIN_MS = 220;
const FINISH_MAX_MS = 700;

/* How far a frontier may travel to land on a word boundary, and how far back it
   may be pulled off an unfinished marker.

   Bounded on purpose. A long unbroken token (a URL, a hash, a stray bracket the
   model never closes) has no safe boundary in it, and a frontier that kept
   looking for one would stall the answer at that character until the stream
   ended. Past this much the frontier gives up and reveals, which costs one
   frame of a half-drawn word at worst. */
const SPLIT_LOOKBACK = 24;
const MARKER_LOOKBACK = 120;

/* How a pack arrives. The thought finishes first, then each card on its own,
   with a pause between them, then the message. A reload already has the set,
   so this pacing is only for the turn that just received it. */
const CARD_LEAD_MS = 420;
const CARD_GAP_MS = 680;

/* The disclosure fold, the same length as `$dur`. Cards wait it out so the
   first card does not arrive while the thought is still closing. */
const THOUGHT_FOLD_MS = 250;

let revealFrame = 0;
let revealAt = 0;
let finishProgress = 0;
let finishDuration = 0;
let finishFrom = 0;

/* Whether this turn held the reply for a narration. The finish uses it once:
   a reply that starts from nothing after the trace is written at the live rate,
   while a tail the visitor has already been watching keeps the short cap. */
let answerWasHeld = false;

/* Set once the narration has handed over, or once this turn has shown it will
   not narrate. Stays set: folding the trace clears the "thinking is over"
   flag, and without this the next frame would take that as more narration
   still to come and stop the reply mid-word. */
let narrationSettled = false;

/* The cards still waiting to be drawn onto the message they belong to. The
   message is the object in the transcript, so each push is the next card. */
let cardTimer = 0;
let cardQueue = [];
let thoughtFoldTimer = 0;

/* Cards are already in from the tool, and they stay here until the thought
   has been drawn and has folded. A pack that arrived during the narration
   was drawing itself on top of that animation. */
let cardsHeldForThought = true;

/* Starts the loop, or leaves it running. Safe to call per send. */
function startReveal() {
  finishProgress = 0;
  finishDuration = 0;
  finishFrom = 0;
  revealAt = 0;
  answerWasHeld = false;
  narrationSettled = false;

  if (!revealFrame) {
    revealFrame = window.requestAnimationFrame(revealTick);
  }
}

function stopReveal() {
  if (revealFrame) {
    window.cancelAnimationFrame(revealFrame);
    revealFrame = 0;
  }

  revealAt = 0;
  finishProgress = 0;
  finishDuration = 0;
  finishFrom = 0;
  answerWasHeld = false;
  narrationSettled = false;
}

/* The frame.

   `dt` is clamped rather than measured raw, because a tab that was hidden has a
   `performance.now()` gap of however long it was away in it, and an unclamped
   gap would reveal the whole backlog in one frame on return, which is exactly
   the dump this module exists to prevent. Clamped, the reveal simply resumes
   where it left off, and a backdrop that was hidden long enough for the answer
   to land finishes in the usual bounded tail when it comes back. */
function revealTick(now) {
  revealFrame = 0;

  const dt = revealAt ? Math.min(64, now - revealAt) : 16.67;
  revealAt = now;

  /* The narration goes first, at the answer's own rate. Once the thinking has
     ended, or the answer itself has, the buffer is final: an unclosed marker is
     the model's text, not a closer that is still on the way, and holding the
     frontier there would leave the turn in flight forever. */
  state.reasoningShown = advance(
    state.reasoningShown,
    state.reasoningText,
    dt,
    REVEAL_CHARS_PER_SEC,
    state.reasoningDone || state.streamDone
  );

  /* The reply stays hidden while the narration is still being written, or while
     more of it may still arrive. The text is already in `streamingText`; only
     the revealed prefix waits. A turn with no narration never blocks, because
     one that does not think never sends the signal that thinking ended. */
  if (narrationBlocking()) {
    answerWasHeld = true;
  } else if (narrationSettled || narrationReady()) {
    narrationSettled = true;

    /* Folded once the narration has actually been shown, not on the token that
       ended the thinking. A narration that arrives faster than it is drawn
       would otherwise close with its last sentence still unwritten, and the
       reply would start over the top of it. The pack waits until that fold
       has finished, so a card never arrives over a thought that is still moving. */
    if (
      cardsHeldForThought &&
      !thoughtFoldTimer &&
      state.reasoningShown.length >= state.reasoningText.length &&
      (state.reasoningDone || state.streamDone || state.reasoningText.length === 0)
    ) {
      state.reasoningDone = false;
      state.thinkOpen = false;
      armCardRelease(state.reasoningText.length > 0 ? THOUGHT_FOLD_MS : 0);
    }

    if (cardsBlocking()) {
      state.streamingShown = '';
    } else if (state.streamDone) {
      revealFinish(dt);
    } else {
      state.streamingShown = advance(state.streamingShown, state.streamingText, dt, REVEAL_CHARS_PER_SEC, false);
    }
  }

  /* The turn is over once the answer is whole, fully revealed, and the
     narration has finished being drawn. The narration is part of the gate
     rather than flushed at the end, so the trace is never cut off mid-sentence
     by the answer landing on top of it. */
  if (
    state.streamDone &&
    !cardsBlocking() &&
    state.streamingShown.length >= state.streamingText.length
  ) {
    stopReveal();
    finishAnswer();

    return;
  }

  revealFrame = window.requestAnimationFrame(revealTick);
}

/* Whether the reply must stay off screen for the narration.

   Caught up to an unfinished narration still blocks: a pause between two
   narration deltas is not the trace being done. The stream ending is the other
   way the narration becomes final, for a turn that thought and then failed to
   send the usual "thinking is over" signal. */
/* The message waits until the pack has been drawn. The queue and the timer
   are the pack still in flight. */
function cardsBlocking() {
  return state.cardsBeforeText && (cardQueue.length > 0 || cardTimer !== 0);
}

function narrationBlocking() {
  if (narrationSettled || (state.reasoningText.length === 0 && !state.reasoningActive)) {
    return false;
  }

  if (state.reasoningShown.length < state.reasoningText.length) {
    return true;
  }

  return !state.reasoningDone && !state.streamDone;
}

/* The reply may start. A narration that has been drawn to its end may, and so
   may a turn that has already begun its answer without ever narrating. Neither
   has happened in the frames before the first token, and those frames must not
   decide the turn has no trace: the narration usually arrives a moment later. */
function narrationReady() {
  if (
    state.reasoningText.length > 0 &&
    state.reasoningShown.length >= state.reasoningText.length &&
    (state.reasoningDone || state.streamDone)
  ) {
    return true;
  }

  if (state.streamingText.length > 0 && state.reasoningText.length === 0 && !state.reasoningActive) {
    return true;
  }

  return state.streamDone && state.reasoningText.length === 0;
}

/* The live catch-up: the approach curve, capped by the rate, never backwards,
   and landed on a boundary the markdown can survive being cut at.

   `closed` means this buffer will not grow again. The word snap may then run
   to the end of it, and the marker pullback is skipped: a closer that has not
   arrived is not going to, and pulling back to an opening `**` with nothing
   left to come holds the frontier on that character forever. The turn would
   stay in flight, and both the send button and the chips wait on that. */
function advance(shown, received, dt, ratePerSec, closed) {
  const backlog = received.length - shown.length;

  if (backlog <= 0) {
    return shown;
  }

  /* Normalised by time so a 120Hz display does not reveal twice as fast as a
     60Hz one: the share covered is per frame, so the share has to be per unit
     of time before it is applied. */
  const approach = 1 - Math.pow(1 - REVEAL_APPROACH, dt / 16.67);
  const capped = (ratePerSec / 1000) * dt;
  const step = Math.max(1, Math.min(backlog * approach, capped));
  let wanted = Math.min(received.length, Math.floor(shown.length + step));

  if (wanted >= received.length) {
    return received;
  }

  /* Finish the word the step landed in, instead of stepping back to the
     boundary already behind the frontier. Stepping back never moves: the next
     frame lands in the same word, steps back to the same place, and a reply
     that contains an ordinary word never reaches its end. */
  wanted = completeWord(received, wanted, closed);

  if (wanted < 0) {
    return shown;
  }

  if (!closed) {
    wanted = Math.max(shown.length, snapOffMarker(received, wanted));
  }

  if (wanted <= shown.length) {
    return shown;
  }

  return received.slice(0, wanted);
}

/* The finish: the remaining backlog, over a bounded duration.

   Eased with the same curve the document's scroll tween uses, because that is
   the only easing the site expresses in JavaScript; the CSS `$ease` is not
   callable from here, and a third curve invented for this one tail is exactly
   the drift to avoid. A distance small enough to need less than the floor still
   takes the floor, which is what makes a one-line answer read as written. */
function revealFinish(dt) {
  const target = state.streamingText.length;

  if (finishDuration === 0) {
    finishFrom = state.streamingShown.length;

    const backlog = Math.max(0, target - finishFrom);

    const atRate = (backlog / REVEAL_CHARS_PER_SEC) * 1000;

    /* A reply that waited out the narration starts from nothing, so it is
       written at the same rate the trace was. The cap is for a short tail the
       visitor has already been watching, which should not turn into a wait
       after the reply has finished arriving. */
    finishDuration = answerWasHeld && finishFrom === 0
      ? Math.max(FINISH_MIN_MS, atRate)
      : Math.min(FINISH_MAX_MS, Math.max(FINISH_MIN_MS, atRate));
  }

  finishProgress = Math.min(1, finishProgress + dt / finishDuration);

  if (finishProgress >= 1) {
    state.streamingShown = state.streamingText;

    return;
  }

  const eased = scrollEase(finishProgress);

  state.streamingShown = state.streamingText.slice(0, Math.round(finishFrom + (target - finishFrom) * eased));
}

/* Where the frontier may safely sit, without cutting a word in half.

   The step lands between two characters of a Latin word more often than not.
   The end of that word is already in the buffer when a space or an ideograph
   follows it, and the frontier takes the whole word: `respo` never has to
   become `response` a letter later. A word that runs off the end of what has
   arrived is still being typed, so the frontier holds, unless `closed` says
   nothing more is coming.

   The lookback is the give-up. A long unbroken token has no boundary within
   it, and a frontier that kept waiting for one would stall the turn on that
   token. Past this many characters it reveals, which costs one frame of a
   half-drawn word at worst. `-1` is the hold. */
function completeWord(text, index, closed) {
  if (index <= 0 || index >= text.length || splitsAt(text, index)) {
    return index;
  }

  let at = index;
  let looked = 0;

  while (at < text.length && looked < SPLIT_LOOKBACK && !splitsAt(text, at)) {
    at += 1;
    looked += 1;
  }

  if (at < text.length && splitsAt(text, at)) {
    return at;
  }

  if (looked >= SPLIT_LOOKBACK) {
    return at;
  }

  return closed ? text.length : -1;
}

function splitsAt(text, at) {
  const before = text.charCodeAt(at - 1);

  if (before === 32 || before === 10 || before === 9) {
    return true;
  }

  if (isIdeograph(before)) {
    return true;
  }

  return isIdeograph(text.charCodeAt(at));
}

/* Everything from the CJK radicals block up: CJK, kana, Hangul and the
   fullwidth forms. A crude test on purpose. What it has to catch is the
   ideographic scripts, which break between characters, and what it must not
   catch is anything Latin, which breaks at spaces. */
function isIdeograph(code) {
  return code >= 0x2e80;
}

/* Pulls the frontier back off a half-written inline marker.

   A frontier that lands inside `**bold**` shows the asterisks for a frame and
   then snaps them into weight, and the same is true of an open backtick or an
   open bracket. Only these three are checked, and only because their literal
   form is the most visible thing an unfinished marker can put on screen; the
   cost of a miss is a frame of punctuation, and anything cleverer would be a
   parser living in a composable. Inside a fence none of it applies, because
   there the markers are the content. */
function snapOffMarker(text, index) {
  if (index <= 0 || index >= text.length) {
    return index;
  }

  const shown = text.slice(0, index);

  /* The text ends inside a fenced block, so what follows is code and every
     marker in it is meant to be read literally. */
  if (countOf(shown, '```') % 2 === 1) {
    return index;
  }

  const markers = [
    { mark: '**', count: countOf(shown, '**') },
    { mark: '`', count: countOf(shown, '`') },
  ];

  for (const { mark, count } of markers) {
    if (count % 2 === 1) {
      const at = shown.lastIndexOf(mark);

      if (at >= 0 && index - at <= MARKER_LOOKBACK) {
        return at;
      }
    }
  }

  const open = shown.lastIndexOf('[');

  if (open >= 0 && index - open <= MARKER_LOOKBACK && shown.indexOf(']', open) === -1) {
    return open;
  }

  return index;
}

function countOf(text, needle) {
  let count = 0;
  let at = text.indexOf(needle);

  while (at !== -1) {
    count += 1;
    at = text.indexOf(needle, at + needle.length);
  }

  return count;
}

export function useChat() {
  if (!localeRef) {
    localeRef = useI18n({ useScope: 'global' }).locale;
  }

  pageRef = usePage();

  return {
    /* Bound straight onto the panel: `v-bind="chatSurface"`. Both call sites use
       this one object rather than repeating the same prop list, so there is one
       shape to keep in step instead of two lists that drift apart. */
    surface: computed(() => ({
      messages: state.messages,
      suggestions: state.suggestions,
      suggestionsStale: state.suggestionsStale,
      chipMode: state.chipMode,
      isTyping: state.isTyping,
      checking: state.checking,
      challengeOpen: state.challengeOpen,
      /* The revealed prefix, not the received text. Same prop names, so the
         surface has no opinion about the pacing and does not need one: it draws
         what it is told is on screen. */
      streamingText: state.streamingShown,
      liveCards: state.liveCards,
      reasoningText: state.reasoningShown,
      reasoningActive: state.reasoningActive,
      reasoningMs: state.reasoningMs,
      openThinks: state.openThinks,
      thinkOpen: state.thinkOpen,
      disabled: state.available === false,
      hydrated: state.hydrated,
      notice: state.notice,
    })),

    /* The composer, two-way bound onto the field with `v-model:draft`. Writable
       rather than read only, because the field is its writer on every
       keystroke. */
    draft: computed({
      get: () => state.draft,
      set: (value) => {
        state.draft = value;
      },
    }),

    activate,
    close,
    prefetch,
    send,
    toggleThink,
    setThinkOpen,
  };
}

/* Asks for the transcript before there is a surface to draw it in.

   Called on the launcher's first hover or focus, which is intent rather than
   interest: the request starts while the visitor is still reaching for the
   button, so the panel usually opens onto a thread that is already in hand and
   never shows the loading state at all. What it preserves is the rule the first
   load is written around, which is that a visitor who never meets the assistant
   never pays for the request. */
function prefetch() {
  loadHistory();
}

/* The transcript. One request until it succeeds.

   `loaded` is set before the request rather than after, so two surfaces
   mounting in the same tick (the AI page's band and the launcher) cannot both
   start one. A failed read clears it, so the next open tries again instead of
   greeting over a transcript that is sitting on the server. `hydrated` is what
   the panel reads, and it is set when the request settles rather than when it
   starts, because until then the panel cannot tell an empty thread from an
   unanswered question. */
function loadHistory() {
  if (state.loaded || naming) {
    return historyReady;
  }

  state.loaded = true;

  historyReady = fetch('/chat/history', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('history');
      }

      return response.json();
    })
    .then((data) => {
      /* Nothing to restore is still an answer to the question the panel asked,
         so the greeting is what it should show. A turn that started while this
         request was in flight is kept: the restore is the prefix, and the
         optimistic tail is the part the server snapshot may not have yet. */
      if (data) {
        state.messages = mergeHistory((data.messages || []).map(mapRow), state.messages);

        if (!state.isTyping) {
          state.suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
          state.chipMode = data.chip_mode === 'choices' ? 'choices' : 'suggestions';
        }

        /* The open traces are keyed by position in the transcript, and the
           transcript has just been replaced, so those positions no longer mean
           what they did. Cleared rather than carried over. A turn in flight
           keeps its own disclosure, which is not keyed by those positions. */
        if (!state.isTyping) {
          state.openThinks = {};
        }

        state.available = data.available !== false;
      }

      state.hydrated = true;
    })
    .catch(() => {
      /* A history that could not be read is not a reason to disable the panel:
         the answers come from a different endpoint and may still work. It is
         still an answer, though, so the panel stops waiting for one. The flag
         is cleared so the next open asks again instead of greeting forever
         over a transcript that is sitting on the server. */
      state.loaded = false;
      state.hydrated = true;
    });

  return historyReady;
}

/* The restored transcript plus any turn the visitor already sent while the
   request was in flight. A pending message the restore already ends with is
   the same turn, not a second copy. */
function mergeHistory(restored, local) {
  const pending = local.filter((message) => message.id == null);

  if (pending.length === 0) {
    return restored;
  }

  if (restored.length < local.length) {
    return local;
  }

  let skip = 0;
  const max = Math.min(restored.length, pending.length);

  for (let size = max; size > 0; size -= 1) {
    let matches = true;

    for (let i = 0; i < size; i += 1) {
      const row = restored[restored.length - size + i];
      const message = pending[i];

      if (!row || row.role !== message.role || row.text !== message.text) {
        matches = false;
        break;
      }
    }

    if (matches) {
      skip = size;
      break;
    }
  }

  return restored.concat(pending.slice(skip));
}

/* A surface came on screen: the launcher's panel opening, or the AI page's box
   mounting. The name is the caller's own, and it is what `close` removes. */
function activate(name) {
  visibleSurfaces.add(name);

  /* History first, then the chips. The history response is what names a new
     visitor, and a chip request that left in the same tick would mint a second
     id. The slower of the two responses would then be the cookie the browser
     kept, and the transcript would be stored under the one it discarded. */
  loadHistory().then(() => {
    /* The chips for the newest turn, but only when the row on screen is not
       already for it: the opening set on a first open, and the set for a reply
       that landed while the panel was closed.

       An open with a current row asks for nothing, and that is the half of the
       flicker that is not the fold's fault. Every open used to ask, which meant
       every open started a request whose only effect on screen was to hide the
       chips until it came back.

       A turn still in flight is left alone too. Its chips do not exist yet, and
       asking now would be answered with the previous turn's set; the send asks
       for them itself once the answer is in. */
    if (!state.isTyping && (state.suggestions.length === 0 || state.suggestionsStale) && visibleSurfaces.has(name)) {
      refreshSuggestions();
    }
  });
}

/* A surface left the screen. Scoped to the name that registered, so closing the
   floating panel on the AI page does not hide the band that is still there. */
function close(name) {
  visibleSurfaces.delete(name);
}

/* Opens or closes one finished trace. The key is the message's index, which is
   the only handle a turn has before the server has named it. */
function toggleThink(index) {
  state.openThinks[index] = !state.openThinks[index];
}

/* Opens or closes the live trace. The fold the model drives goes through here
   too, so a fold by the visitor and a fold on the answer's first token write
   the same state rather than two. */
function setThinkOpen(value) {
  state.thinkOpen = value;
}

/* Asks the server for the newest set of chips. Safe to call more than once per
   turn: they are stored on the assistant message they follow, so the second
   call is answered from the row rather than from the model. */
function refreshSuggestions() {
  if (visibleSurfaces.size === 0) {
    suggestionQueued = false;

    return;
  }

  if (state.suggesting) {
    suggestionQueued = true;

    return;
  }

  suggestionQueued = false;

  const epoch = suggestionEpoch;

  state.suggesting = true;

  fetch('/chat/suggestions', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrfToken(),
    },
    body: JSON.stringify({
      locale: currentLocale(),
      page: window.location.pathname,
    }),
  })
    .then((response) => (response && response.ok ? response.json() : null))
    .then((data) => {
      /* A question went out after this request did. Its chips belong to the
         turn before that question, and applying them would put the old row
         back under an answer that is owed a new one. */
      if (epoch !== suggestionEpoch) {
        return;
      }

      const items = data && Array.isArray(data.items) ? data.items : [];

      /* An empty set is not news. The previous chips stay put rather than the
         row clearing and reflowing under the visitor's cursor. */
      if (items.length) {
        state.suggestions = items;
        state.chipMode = data.mode === 'choices' ? 'choices' : 'suggestions';
      }

      /* The row is the newest one there is again, whether the server sent a
         replacement or had nothing to add. An empty answer leaves the chips
         that were already there, and those become the row rather than the fold
         staying on for a set that is never coming. */
      state.suggestionsStale = false;
    })
    .catch(() => {
      if (epoch !== suggestionEpoch) {
        return;
      }

      /* A failed chip generation is never surfaced: the reply arrived on its own
         stream and is unaffected, and the visitor keeps the chips they had.
         Staleness clears for the same reason an empty answer clears it. */
      state.suggestionsStale = false;
    })
    .then(() => {
      state.suggesting = false;

      if (suggestionQueued) {
        refreshSuggestions();
      }
    });
}

/* Sends one turn and reads the answer off its own stream. */
async function send(text) {
  const value = String(text == null ? '' : text).trim();

  /* `sendLock` is what blocks a second click during the check. `isTyping` cannot
     do that on its own: it is the loading bubble, and it stays down until the
     token is in hand so leaving the page does not leave a bubble behind. */
  if (value === '' || state.isTyping || state.available === false || sendLock) {
    return;
  }

  /* The check finishes before the turn starts. A bubble that begins while the
     visitor is still on the checkbox stays on screen if they leave the page
     before ticking it, because this module outlives the box that held the
     widget. Waiting first means leaving abandons the send, and the next page
     opens on the thread as it was. */
  sendLock = true;
  state.checking = true;
  state.challengeOpen = false;

  let turnstile = '';
  const sitekey = turnstileSitekey();

  try {
    await historyReady;

    /* No site key means this environment is not running the check. The send
       goes straight through, and the server accepts it only where the check
       is off. */
    if (sitekey) {
      turnstile = await turnstileToken('chat_message', () => {
        state.challengeOpen = true;
      });
    }
  } catch (error) {
    sendLock = false;
    state.checking = false;
    state.challengeOpen = false;

    /* Leaving the page drops the check on purpose, and that is not a failure
       to tell them about. A check that actually failed stays on this thread,
       under the composer, the same sentence as the enquiry form. */
    if (!error || error.reason !== 'abandoned') {
      state.notice = 'turnstile';
      resetTurnstile('chat_message');
    }

    return;
  }

  /* Cleared only once the check has passed, and only when the field still holds
     the question that was sent. Walking away from the checkbox leaves the draft
     where it was. The lock stays until the bubble is up, so this turn and the
     next one cannot overlap. */
  if (state.draft.trim() === value) {
    state.draft = '';
  }

  state.checking = false;
  state.challengeOpen = false;
  /* A pack still arriving belongs to the turn before this question. Put the
     rest of it down now, so the new message does not land in the middle of it. */
  flushCards();
  state.messages.push({ id: null, role: 'user', text: value });
  suggestionEpoch += 1;
  state.notice = '';
  state.streamingText = '';
  state.streamingShown = '';
  state.reasoningText = '';
  state.reasoningShown = '';
  state.reasoningActive = false;
  state.reasoningDone = false;
  state.streamDone = false;
  state.reasoningMs = 0;
  state.pendingCards = [];
  state.liveCards = [];
  state.cardsBeforeText = false;
  cardsHeldForThought = true;
  clearThoughtFold();
  /* The row on screen was generated for the turn before this one, so it is out
     of date from here and folds away for the wait. Cleared when the set for
     this answer arrives, or put back by the branch at the end if the turn never
     lands. */
  state.suggestionsStale = true;
  /* Re-armed here rather than by a watcher in the surface, so the trace starts
     open in every drawing of this thread at the same moment. A send from either
     surface while the other is open has to open it there too. */
  state.thinkOpen = true;
  state.isTyping = true;
  sendLock = false;

  /* The reveal starts with the turn rather than with the first token. Frames
     with nothing to show cost nothing, and one start site means there is no way
     for an answer to arrive with no loop running. */
  startReveal();

  let answered = false;
  let rejected = false;

  naming = true;

  try {
    /* The history response is what sets the visitor cookie. Waiting for it
       means this request carries that cookie instead of minting another. */
    const response = await fetch('/chat/message', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-CSRF-TOKEN': csrfToken(),
      },
      body: JSON.stringify({
        message: value,
        locale: currentLocale(),
        'cf-turnstile-response': turnstile,
      }),
    });

    if (response.status === 503) {
      /* A missing key, a rotated token or a dead gateway. Not a fault of the
         visitor's, so it becomes the surface's own unavailable state rather
         than a line they cannot act on. The question was not stored. */
      state.available = false;
      rejected = true;
    } else if (response.status === 429) {
      /* Hitting the per-minute ceiling is a normal outcome for a burst of
         messages, so it gets its own line rather than the generic failure one.
         Waiting is the remedy: the window clears, and the same thread still
         accepts the next message. The question was not stored. */
      state.notice = 'throttled';
      rejected = true;
    } else if (response.status === 422) {
      /* A token the server refused gets the same line as a check that never
         returned one. Any other 422 is still the generic failure. */
      rejected = true;
      state.notice = 'failed';

      try {
        const body = await response.json();

        if (body && body.errors && body.errors['cf-turnstile-response']) {
          state.notice = 'turnstile';
        }
      } catch (parseError) {
        /* The generic line stays. */
      }
    } else if (!response.ok || !response.body) {
      state.notice = 'failed';
      rejected = true;
    } else {
      trackChat();
      answered = await readStream(response.body);
    }
  } catch (error) {
    state.notice = 'failed';
  } finally {
    resetTurnstile('chat_message');
    naming = false;

    /* Only a turn that never completed is cleaned up here. One that did is still
       being revealed, and the reveal owns the commit: it is the thing that
       decides when the transcript takes the answer, so clearing the buffers here
       would empty the answer out from under the animation still drawing it.

       A stream that died part way leaves no partial bubble at all, which is why
       the failure path clears everything it set. The server writes only a reply
       that reached completion, so half of one shown here would be text in the
       transcript that a reload would not find, and there is nothing to reveal
       anyway. */
    if (!answered) {
      stopReveal();
      state.isTyping = false;
      state.streamingText = '';
      state.streamingShown = '';
      state.reasoningText = '';
      state.reasoningShown = '';
      state.reasoningActive = false;
      state.reasoningDone = false;
      state.streamDone = false;
      state.liveCards = [];
      state.cardsBeforeText = false;
      cardsHeldForThought = true;
      clearThoughtFold();

      /* A rejection never reached the transcript. Leaving the bubble up would
         show a question a reload would not find. A stream that started did
         store the question, so that bubble stays. */
      if (rejected) {
        dropOptimistic(value);
      }

      if (state.notice === '') {
        state.notice = 'failed';
      }
    }
  }

  /* Every way out of the turn lands here, and that is the point. The row folded
     away for the wait is put back unless an answer is about to bring the set
     that replaces it, which is the one thing that ends the wait. The checks
     above are written as branches rather than as early returns because the row
     has to be restored on each of the failure paths too, and three `return`s
     would each have had to remember that on its own. */
  if (answered) {
    refreshSuggestions();
  } else {
    state.suggestionsStale = false;
  }
}

/* Reads our own small contract off the response body: `delta`, `reasoning`,
   `reasoning_complete`, `answer_complete` and `error`. The browser is
   deliberately not told any provider event name, so this keeps working when
   tools and their status events are added later.
   Returns whether the reply reached completion. */
async function readStream(body) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answered = false;

  for (;;) {
    const { value, done } = await reader.read();

    if (done) {
      break;
    }

    /* Normalised before anything else, because a proxy is free to hand the
       frames back with CRLF line endings and a reader that only looks for a
       blank `\n` would sit on the whole reply until the stream ended. */
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

    let edge = buffer.indexOf('\n\n');

    while (edge !== -1) {
      answered = handleFrame(buffer.slice(0, edge)) || answered;
      buffer = buffer.slice(edge + 2);
      edge = buffer.indexOf('\n\n');
    }
  }

  if (buffer.trim() !== '') {
    answered = handleFrame(buffer) || answered;
  }

  return answered;
}

function handleFrame(frame) {
  let data = '';

  frame.split('\n').forEach((line) => {
    if (line.startsWith('data:')) {
      data += line.slice(5).trim();
    }
  });

  if (data === '') {
    return false;
  }

  let event = null;

  try {
    event = JSON.parse(data);
  } catch (error) {
    return false;
  }

  if (!event || typeof event !== 'object') {
    return false;
  }

  if (event.type === 'delta') {
    state.streamingText += String(event.text == null ? '' : event.text);

    return false;
  }

  /* The narration. It arrives ahead of the answer and never mixed into it, so
     it accumulates into its own buffer rather than into the bubble. */
  if (event.type === 'reasoning') {
    state.reasoningActive = true;
    state.reasoningText += String(event.text == null ? '' : event.text);

    return false;
  }

  /* Sent once the answer's first token arrives, which is what the panel reads
     as "the thinking is over". The figure is the server's, the same one stored
     on the row, and the fold is armed. The fold waits until the narration has
     been shown in full, and the reply stays hidden until that same moment, so a
     fast answer cannot draw itself over a trace that is still being written. */
  if (event.type === 'reasoning_complete') {
    state.reasoningActive = false;
    const ms = Number(event.reasoning_ms);
    state.reasoningMs = Number.isFinite(ms) ? Math.max(0, ms) : 0;
    state.reasoningDone = true;

    return false;
  }

  if (event.type === 'cards' && Array.isArray(event.cards)) {
    state.pendingCards = state.pendingCards.concat(event.cards);
    state.cardsBeforeText = true;
    state.streamingShown = '';
    enqueueLiveCards(event.cards);

    if (!revealFrame) {
      startReveal();
    }

    return false;
  }

  if (event.type === 'answer_complete') {
    /* The reply is whole and persisted, so what has arrived is now the answer
       and there is nothing left to wait for on the wire. The turn is not over
       yet, though: both frontiers are still being drawn, and the transcript
       takes it when they reach their ends. That is what stops the finished
       answer appearing while it is still being revealed.

       The narration is deliberately not flushed here. It is written at the
       answer's own rate and may still be mid-sentence, so pinning it to its end
       on this token would put the rest of it on screen in one frame. */
    state.streamDone = true;

    if (!revealFrame) {
      startReveal();
    }

    return true;
  }

  if (event.type === 'error') {
    /* The server sends its own sentence, but the panel words the state, so the
       key is what is kept rather than a message the surface did not write. */
    state.notice = 'failed';

    return false;
  }

  return false;
}

/* Moves the revealed answer into the transcript.

   Called by the reveal loop once the frontier has reached the end of the
   received text, which is always after `answer_complete` and never before, so
   the answer is real and persisted by the time it becomes a message. The
   received buffers are the authority here rather than the revealed ones: they
   are equal at this point, and one of them is the whole answer by definition
   while the other is a string that was being sliced a frame earlier.

   The turn is flagged as settled, which is the surface's cue not to replay the
   bubble's entrance. The visitor has just watched this bubble being written, so
   animating it in again at the moment it finishes would read as the answer
   arriving twice. */
function finishAnswer() {
  const text = state.streamingText;
  const cards = state.liveCards.slice();
  const reasoning = state.reasoningText;
  const reasoningMs = state.reasoningMs;

  state.streamingText = '';
  state.streamingShown = '';
  state.reasoningText = '';
  state.reasoningShown = '';
  state.reasoningActive = false;
  state.reasoningDone = false;
  state.streamDone = false;
  state.isTyping = false;
  state.pendingCards = [];
  state.liveCards = [];
  state.cardsBeforeText = false;

  if (text.trim() !== '' || cards.length > 0) {
    const message = {
      id: null,
      role: 'bot',
      text,
      settled: true,
      cards,

      /* Carried on the message so the trace stays beside the answer it produced.
         Null when the model wrote none, which is what keeps the disclosure off
         an answer that had no thinking to show. The same pair is what history
         restores, so a reload opens onto the trace that was saved. */
      reasoning: reasoning.trim() === '' ? null : { text: reasoning, ms: reasoningMs },
    };

    state.messages.push(message);
  }
}

/* Adds a pack to the live row, one card at a time. The message stays hidden
   until this queue is empty. */
function enqueueLiveCards(cards) {
  if (!cards.length) {
    return;
  }

  cardQueue = cardQueue.concat(cards);

  if (!cardsHeldForThought && !cardTimer) {
    cardTimer = window.setTimeout(revealNextCard, CARD_LEAD_MS);
  }
}

/* The thought has finished. `delay` is the fold, or zero when this turn had
   nothing to fold. The queue may still be empty: a pack that arrives after
   this starts on its own. */
function armCardRelease(delay) {
  clearThoughtFold();

  if (delay > 0) {
    thoughtFoldTimer = window.setTimeout(() => {
      thoughtFoldTimer = 0;
      releaseHeldCards();
    }, delay);

    return;
  }

  releaseHeldCards();
}

function releaseHeldCards() {
  cardsHeldForThought = false;

  if (cardQueue.length > 0 && !cardTimer) {
    cardTimer = window.setTimeout(revealNextCard, CARD_LEAD_MS);
  }
}

function clearThoughtFold() {
  if (thoughtFoldTimer) {
    window.clearTimeout(thoughtFoldTimer);
    thoughtFoldTimer = 0;
  }
}

function revealNextCard() {
  cardTimer = 0;

  if (cardQueue.length === 0) {
    return;
  }

  state.liveCards.push(cardQueue.shift());

  if (cardQueue.length === 0) {
    return;
  }

  cardTimer = window.setTimeout(revealNextCard, CARD_GAP_MS);
}

/* The rest of a pack, at once. Used when the next question starts before the
   last card has been drawn, so none of the pack is left off the transcript. */
function flushCards() {
  if (cardTimer) {
    window.clearTimeout(cardTimer);
    cardTimer = 0;
  }

  if (cardQueue.length > 0) {
    const last = state.messages[state.messages.length - 1];

    if (last && last.role === 'bot' && Array.isArray(last.cards)) {
      cardQueue.forEach((card) => last.cards.push(card));
    } else {
      cardQueue.forEach((card) => state.liveCards.push(card));
    }
  }

  cardQueue = [];
}

function dropOptimistic(value) {
  const last = state.messages[state.messages.length - 1];

  if (last && last.role === 'user' && last.id == null && last.text === value) {
    state.messages.pop();
  }
}

function mapRow(row) {
  const reasoning = typeof row.reasoning === 'string' ? row.reasoning.trim() : '';

  return {
    id: row.id,
    role: row.role === 'assistant' ? 'bot' : 'user',
    text: String(row.content == null ? '' : row.content),
    cards: Array.isArray(row.cards) ? row.cards : [],
    reasoning: reasoning === '' ? null : {
      text: row.reasoning,
      ms: Number(row.reasoning_ms) || 0,
    },
  };
}

function currentLocale() {
  return localeRef ? localeRef.value : 'en-MY';
}

/* The token is in the document head rather than in the bundle, which is where
   the rest of the site's forms read it from too. */
function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');

  return meta ? meta.getAttribute('content') : '';
}
